import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Spinner, Banner, Select, DataTable, Popover, Button, Checkbox, Scrollable, TextField, ButtonGroup } from '@shopify/polaris';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Line, ReferenceLine } from 'recharts';
import { useAuthStore } from '../../context/useStore';
import { getRequest } from '../../shared/components/functions';
import './styles.css';

// Hardcoded allowed user IDs - must match server
const ALLOWED_USER_IDS = [3, 47, 10, 36, 105];

// Machine options
const MACHINE_OPTIONS = [
  { label: 'Greg2', value: 'Greg2' },
  { label: 'Jack2', value: 'Jack2' },
  { label: 'Greg3 (coming soon)', value: 'Greg3' },
  { label: 'Jack3 (coming soon)', value: 'Jack3' },
  { label: 'Eva (coming soon)', value: 'Eva' }
];

// ProductID to machine name mapping (must match server MACHINE_PRODUCTS)
const PRODUCT_ID_TO_MACHINE = {
  10956: 'Greg2',
  10957: 'Jack2'
};

// Machine colors - neutral tones
const MACHINE_COLORS = {
  Greg2: '#374151',
  Jack2: '#6b7280',
  Greg3: '#9ca3af',
  Jack3: '#4b5563',
  Eva: '#1f2937'
};

const CHART_COLORS = {
  sold: '#374151',
  invoiced: '#9ca3af',
  goal: '#d1d5db'
};

// Generate year options from 2020 to current year
const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear; year >= 2020; year--) {
    years.push({ label: String(year), value: String(year) });
  }
  return years;
};

const PERIOD_OPTIONS = [
  { label: 'Month', value: 'MONTH' },
  { label: 'Quarter', value: 'QUARTER' },
  { label: 'Year', value: 'YEAR' }
];

// Sales Overview Tab Component
function SalesOverviewTab({ selectedYear, setSelectedYear, yearOptions }) {
  const [summary, setSummary] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [machineData, setMachineData] = useState([]);
  const [monthlyByMachine, setMonthlyByMachine] = useState([]);
  const [yearlyTotals, setYearlyTotals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yearlyLoading, setYearlyLoading] = useState(true);

  // Fetch yearly totals once (doesn't depend on selected year)
  useEffect(() => {
    async function fetchYearlyTotals() {
      setYearlyLoading(true);
      const yearlyRes = await getRequest('/api/pm-dashboard/yearly-totals');
      setYearlyTotals(yearlyRes || []);
      setYearlyLoading(false);
    }
    fetchYearlyTotals();
  }, []);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      const [summaryRes, monthlyRes, machineRes, monthlyMachineRes] = await Promise.all([
        getRequest(`/api/pm-dashboard/summary?year=${selectedYear}`),
        getRequest(`/api/pm-dashboard/monthly?year=${selectedYear}`),
        getRequest(`/api/pm-dashboard/by-machine?year=${selectedYear}`),
        getRequest(`/api/pm-dashboard/monthly-by-machine?year=${selectedYear}`)
      ]);
      setSummary(summaryRes);
      setMonthlyData(monthlyRes || []);
      setMachineData(machineRes || []);
      setMonthlyByMachine(monthlyMachineRes || []);
      setLoading(false);
    }
    fetchData();
  }, [selectedYear]);

  if (loading) {
    return (
      <div className="pm-tab-loading">
        <Spinner size="small" />
      </div>
    );
  }

  const currentYear = new Date().getFullYear();
  const now = new Date();
  const startOfYear = new Date(currentYear, 0, 1);
  const dayOfYear = Math.floor((now - startOfYear) / (1000 * 60 * 60 * 24)) + 1;
  const daysInYear = currentYear % 4 === 0 && (currentYear % 100 !== 0 || currentYear % 400 === 0) ? 366 : 365;
  const isCurrentYear = parseInt(selectedYear) === currentYear;

  const goalProgress = summary ? (summary.totalSold / summary.goal) * 100 : 0;
  const monthlyGoal = Math.ceil(1000 / 12);
  const avgDailySales = summary?.totalSold ? summary.totalSold / dayOfYear : 0;
  const projectedYearEnd = Math.round(avgDailySales * daysInYear);

  const tooltipStyle = {
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    color: '#374151',
    fontSize: '11px',
    padding: '8px'
  };

  return (
    <div className="pm-grid">
      {/* KPI Cards */}
      <div className="pm-kpi-section">
        <div className="pm-kpi-card pm-kpi-sold">
          <div className="pm-kpi-icon">📦</div>
          <div className="pm-kpi-content">
            <span className="pm-kpi-label">Sold</span>
            <span className="pm-kpi-value">{summary?.totalSold || 0}</span>
            <span className="pm-kpi-subtext">Order confirmations</span>
          </div>
        </div>

        <div className="pm-kpi-card pm-kpi-invoiced">
          <div className="pm-kpi-icon">💰</div>
          <div className="pm-kpi-content">
            <span className="pm-kpi-label">Invoiced</span>
            <span className="pm-kpi-value">{summary?.totalInvoiced || 0}</span>
            <span className="pm-kpi-subtext">Revenue realized</span>
          </div>
        </div>

        <div className="pm-kpi-card pm-kpi-goal">
          <div className="pm-kpi-icon">🎯</div>
          <div className="pm-kpi-content">
            <span className="pm-kpi-label">Goal</span>
            <span className="pm-kpi-value">{summary?.goal || 1000}</span>
            <span className="pm-kpi-subtext">{goalProgress.toFixed(1)}% done</span>
          </div>
        </div>

        {isCurrentYear && (
          <div className="pm-kpi-card pm-kpi-projection">
            <div className="pm-kpi-icon">📈</div>
            <div className="pm-kpi-content">
              <span className="pm-kpi-label">Projected</span>
              <span className="pm-kpi-value">{projectedYearEnd}</span>
              <span className="pm-kpi-subtext">Year-end pace</span>
            </div>
          </div>
        )}
      </div>

      {/* Machine Breakdown */}
      <div className="pm-machine-section">
        <h3>By Machine</h3>
        <div className="pm-machine-grid">
          {machineData.map((machine) => (
            <div key={machine.machine} className={`pm-machine-card ${machine.status === 'no_ref_yet' ? 'pm-machine-inactive' : ''}`}>
              <div className="pm-machine-header" style={{ borderColor: MACHINE_COLORS[machine.machine] || '#4b5563' }}>
                <span className="pm-machine-name">{machine.machine}</span>
                {machine.status === 'no_ref_yet' && <span className="pm-machine-badge">TBD</span>}
              </div>
              <div className="pm-machine-stats">
                <div className="pm-machine-stat">
                  <span className="pm-stat-value">{machine.sold}</span>
                  <span className="pm-stat-label">Sold</span>
                </div>
                <div className="pm-machine-stat">
                  <span className="pm-stat-value">{machine.invoiced}</span>
                  <span className="pm-stat-label">Inv</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts Row */}
      <div className="pm-charts-row">
        <div className="pm-card pm-chart-card">
          <h3>Monthly Sold by Machine</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthlyByMachine} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="monthName" stroke="#9ca3af" tick={{ fill: '#6b7280', fontSize: 10 }} />
              <YAxis stroke="#9ca3af" tick={{ fill: '#6b7280', fontSize: 10 }} width={30} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
                  return (
                    <div style={tooltipStyle}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                      {payload.map((entry, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                          <span style={{ color: entry.color }}>{entry.name}:</span>
                          <span>{entry.value}</span>
                        </div>
                      ))}
                      <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 4, paddingTop: 4, fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
                        <span>Total:</span>
                        <span>{total}</span>
                      </div>
                    </div>
                  );
                }}
              />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <ReferenceLine y={84} stroke="#dc2626" strokeDasharray="4 4" strokeWidth={1.5} label={{ value: '84', position: 'right', fill: '#dc2626', fontSize: 10 }} />
              {Object.entries(MACHINE_COLORS).map(([machine, color]) => (
                <Bar key={machine} dataKey={`${machine}_sold`} name={machine} stackId="a" fill={color} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="pm-card pm-chart-card">
          <h3>Monthly Performance</h3>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={monthlyData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="monthName" stroke="#9ca3af" tick={{ fill: '#6b7280', fontSize: 10 }} />
              <YAxis stroke="#9ca3af" tick={{ fill: '#6b7280', fontSize: 10 }} width={30} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <Bar dataKey="sold" name="Sold" fill={CHART_COLORS.sold} radius={[2, 2, 0, 0]} />
              <Bar dataKey="invoiced" name="Invoiced" fill={CHART_COLORS.invoiced} radius={[2, 2, 0, 0]} />
              <Line type="monotone" dataKey={() => monthlyGoal} name="Target" stroke={CHART_COLORS.goal} strokeWidth={1} strokeDasharray="4 4" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Yearly Totals Overview */}
      <div className="pm-charts-row">
        <div className="pm-card pm-chart-card">
          <h3>Yearly Totals Overview</h3>
          {yearlyLoading ? (
            <div className="pm-tab-loading">
              <Spinner size="small" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={yearlyTotals} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="year" stroke="#9ca3af" tick={{ fill: '#6b7280', fontSize: 10 }} />
                <YAxis stroke="#9ca3af" tick={{ fill: '#6b7280', fontSize: 10 }} width={40} />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div style={tooltipStyle}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                        {payload.map((entry, idx) => (
                          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                            <span style={{ color: entry.color }}>{entry.name}:</span>
                            <span>{entry.value}</span>
                          </div>
                        ))}
                      </div>
                    );
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <ReferenceLine y={1000} stroke="#dc2626" strokeDasharray="4 4" strokeWidth={1.5} label={{ value: 'Goal: 1000', position: 'right', fill: '#dc2626', fontSize: 10 }} />
                <Bar dataKey="sold" name="Sold" fill={CHART_COLORS.sold} radius={[2, 2, 0, 0]} />
                <Bar dataKey="invoiced" name="Invoiced" fill={CHART_COLORS.invoiced} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div></div>
      </div>
    </div>
  );
}

// All month names for ensuring complete data
const ALL_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Generate month periods in YYYY-MM format for a given year
const generateMonthPeriods = (year) => {
  return Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);
};

// Invoice Analysis Tab Component
function InvoiceAnalysisTab({ yearOptions }) {
  const currentYear = new Date().getFullYear();
  const [selectedMachines, setSelectedMachines] = useState(['Greg2', 'Jack2']);
  const [selectedYears, setSelectedYears] = useState([String(currentYear)]); // Multi-year support
  const [selectedCountries, setSelectedCountries] = useState(['0']); // 0 = ALL
  const [groupBy, setGroupBy] = useState('MONTH');
  const [availableCountries, setAvailableCountries] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [machinePopoverActive, setMachinePopoverActive] = useState(false);
  const [yearPopoverActive, setYearPopoverActive] = useState(false);
  const [countryPopoverActive, setCountryPopoverActive] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [dataMode, setDataMode] = useState('invoices'); // 'invoices' or 'sales'

  // Fetch available countries for all active machines (Greg2, Jack2 for now)
  useEffect(() => {
    async function fetchCountries() {
      // Fetch countries for all machines that have product IDs (use all years for complete country list)
      const yearsParam = selectedYears.includes('0') ? '' : selectedYears.join(',');
      const res = await getRequest(`/api/pm-dashboard/invoiced-analysis?years=${yearsParam}&machines=Greg2,Jack2&groupBy=YEAR`);
      if (res?.countries) {
        setAvailableCountries(res.countries); // [{id, name}, ...]
      }
    }
    fetchCountries();
  }, [selectedYears]);

  // Fetch data when filters change
  useEffect(() => {
    async function fetchData() {
      if (selectedMachines.length === 0 || selectedYears.length === 0) {
        setChartData([]);
        setTableData([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const machinesParam = selectedMachines.join(',');
      const yearsParam = selectedYears.includes('0') ? '' : selectedYears.join(',');
      const countriesParam = selectedCountries.includes('0') ? '' : `&countries=${selectedCountries.join(',')}`;
      const modeParam = `&mode=${dataMode}`;

      try {
        const [chartRes, analysisRes] = await Promise.all([
          getRequest(`/api/pm-dashboard/invoiced-chart?years=${yearsParam}&groupBy=${groupBy}&machines=${machinesParam}${countriesParam}${modeParam}`),
          getRequest(`/api/pm-dashboard/invoiced-analysis?years=${yearsParam}&groupBy=${groupBy}&machines=${machinesParam}${countriesParam}${modeParam}`)
        ]);

        // Ensure all months are present when grouping by MONTH (only for single year)
        let processedChartData = chartRes || [];
        if (groupBy === 'MONTH' && selectedYears.length === 1 && !selectedYears.includes('0')) {
          const monthPeriods = generateMonthPeriods(selectedYears[0]);
          const existingPeriods = new Map(processedChartData.map((d) => [d.period, d]));
          const completeData = monthPeriods.map((period) => {
            if (existingPeriods.has(period)) {
              return existingPeriods.get(period);
            }
            // Create empty entry for missing month
            const emptyEntry = { period };
            selectedMachines.forEach((m) => {
              emptyEntry[m] = 0;
            });
            return emptyEntry;
          });
          processedChartData = completeData;
        }

        setChartData(processedChartData);
        setTableData(analysisRes?.data || []);
      } catch (err) {
        console.error('Error fetching invoice analysis data:', err);
        setChartData([]);
        setTableData([]);
      }
      setLoading(false);
    }
    fetchData();
  }, [selectedYears, selectedMachines, selectedCountries, groupBy, dataMode]);

  const toggleMachine = useCallback((machine) => {
    setSelectedMachines((prev) => (prev.includes(machine) ? prev.filter((m) => m !== machine) : [...prev, machine]));
  }, []);

  const toggleYear = useCallback(
    (year) => {
      if (year === '0') {
        // Toggle "All" - if clicking All, select only All
        setSelectedYears(['0']);
      } else {
        setSelectedYears((prev) => {
          const withoutAll = prev.filter((y) => y !== '0');
          if (prev.includes(year)) {
            const newSelection = withoutAll.filter((y) => y !== year);
            return newSelection.length === 0 ? [String(currentYear)] : newSelection;
          } else {
            return [...withoutAll, year];
          }
        });
      }
    },
    [currentYear]
  );

  const toggleCountry = useCallback((countryId) => {
    if (countryId === '0') {
      // Toggle "All" - if clicking All, select only All
      setSelectedCountries(['0']);
    } else {
      setSelectedCountries((prev) => {
        const withoutAll = prev.filter((c) => c !== '0');
        if (prev.includes(countryId)) {
          const newSelection = withoutAll.filter((c) => c !== countryId);
          return newSelection.length === 0 ? ['0'] : newSelection;
        } else {
          return [...withoutAll, countryId];
        }
      });
    }
  }, []);

  const countryOptions = [{ label: 'All Countries', value: '0' }, ...availableCountries.map((c) => ({ label: c.name, value: String(c.id) }))];
  const yearOptionsWithAll = [{ label: 'All Years', value: '0' }, ...yearOptions];

  // Filter countries based on search
  const filteredCountryOptions = useMemo(() => {
    if (!countrySearch.trim()) return countryOptions;
    const searchLower = countrySearch.toLowerCase();
    return countryOptions.filter((opt) => opt.value === '0' || opt.label.toLowerCase().includes(searchLower));
  }, [countryOptions, countrySearch]);

  const machineButtonLabel =
    selectedMachines.length === MACHINE_OPTIONS.length
      ? 'All machines'
      : selectedMachines.length === 0
      ? 'Select machines'
      : `${selectedMachines.length} machine${selectedMachines.length > 1 ? 's' : ''}`;

  const yearButtonLabel = selectedYears.includes('0') ? 'All years' : `${selectedYears.length} year${selectedYears.length > 1 ? 's' : ''}`;
  const countryButtonLabel = selectedCountries.includes('0') ? 'All countries' : `${selectedCountries.length} countr${selectedCountries.length > 1 ? 'ies' : 'y'}`;

  // Get selected country names for display
  const selectedCountryNames = useMemo(() => {
    if (selectedCountries.includes('0')) return [];
    return selectedCountries.map((id) => availableCountries.find((c) => String(c.id) === id)?.name).filter(Boolean);
  }, [selectedCountries, availableCountries]);

  // Get selected year labels for display
  const selectedYearLabels = useMemo(() => {
    if (selectedYears.includes('0')) return [];
    return selectedYears.sort((a, b) => b - a); // Sort descending
  }, [selectedYears]);

  // Calculate total from chart data
  const grandTotal = useMemo(() => {
    return chartData.reduce((sum, row) => {
      const rowTotal = selectedMachines.reduce((machineSum, machine) => machineSum + (row[machine] || 0), 0);
      return sum + rowTotal;
    }, 0);
  }, [chartData, selectedMachines]);

  // Detect if data has country breakdown
  const hasCountryBreakdown = tableData.length > 0 && tableData[0].countryName !== undefined;

  // Prepare table rows - pivot table style when multiple countries selected
  const { tableRows, tableHeadings, tableTotals } = useMemo(() => {
    if (hasCountryBreakdown) {
      // PIVOT TABLE: Rows = Countries + Machines, Columns = Periods
      // First, collect all unique periods
      const allPeriods = new Set();
      const byCountryMachine = {};

      tableData.forEach((row) => {
        const countryKey = row.countryName || 'Unknown';
        const period = row.period;
        const machineName = PRODUCT_ID_TO_MACHINE[row.ProductID] || row.ProductName;
        allPeriods.add(period);

        // Group by country -> machine -> period
        if (!byCountryMachine[countryKey]) {
          byCountryMachine[countryKey] = { machines: {}, countryTotal: {} };
        }
        if (!byCountryMachine[countryKey].machines[machineName]) {
          byCountryMachine[countryKey].machines[machineName] = { periods: {}, total: 0 };
        }
        if (!byCountryMachine[countryKey].machines[machineName].periods[period]) {
          byCountryMachine[countryKey].machines[machineName].periods[period] = 0;
        }
        byCountryMachine[countryKey].machines[machineName].periods[period] += row.quantity;
        byCountryMachine[countryKey].machines[machineName].total += row.quantity;

        // Country totals per period
        if (!byCountryMachine[countryKey].countryTotal[period]) {
          byCountryMachine[countryKey].countryTotal[period] = 0;
        }
        byCountryMachine[countryKey].countryTotal[period] += row.quantity;
      });

      // Sort periods
      const sortedPeriods = Array.from(allPeriods).sort();

      // Build rows: country header, then machine rows, then country subtotal
      const rows = [];
      const grandTotals = { periods: {}, total: 0 };
      sortedPeriods.forEach((p) => {
        grandTotals.periods[p] = 0;
      });

      Object.entries(byCountryMachine)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .forEach(([country, countryData]) => {
          let countryGrandTotal = 0;
          let isFirstMachineRow = true;

          // Machine rows for this country
          selectedMachines.forEach((machine) => {
            const machineData = countryData.machines[machine];
            if (machineData) {
              // Only show country name on first row, empty for others (visual grouping)
              const row = [isFirstMachineRow ? country : '', machine];
              sortedPeriods.forEach((period) => {
                const val = machineData.periods[period] || 0;
                row.push(val);
              });
              row.push(machineData.total);
              countryGrandTotal += machineData.total;
              rows.push(row);
              isFirstMachineRow = false;
            }
          });

          // Country subtotal row (bold styling via special marker)
          const subtotalRow = [`► ${country}`, 'TOTAL'];
          sortedPeriods.forEach((period) => {
            const val = countryData.countryTotal[period] || 0;
            subtotalRow.push(val);
            grandTotals.periods[period] += val;
          });
          subtotalRow.push(countryGrandTotal);
          grandTotals.total += countryGrandTotal;
          rows.push(subtotalRow);
        });

      // Grand totals row
      const totals = ['Grand Total', '', ...sortedPeriods.map((p) => grandTotals.periods[p]), grandTotals.total];

      return {
        tableRows: rows,
        tableHeadings: ['Country', 'Machine', ...sortedPeriods, 'Total'],
        tableTotals: totals
      };
    } else {
      // Simple grouping by period only (standard view)
      const grouped = {};
      tableData.forEach((row) => {
        if (!grouped[row.period]) {
          grouped[row.period] = { period: row.period, total: 0 };
        }
        const machineName = PRODUCT_ID_TO_MACHINE[row.ProductID] || row.ProductName;
        grouped[row.period][machineName] = (grouped[row.period][machineName] || 0) + row.quantity;
        grouped[row.period].total += row.quantity;
      });

      const rows = Object.values(grouped)
        .sort((a, b) => a.period.localeCompare(b.period))
        .map((row) => [row.period, ...selectedMachines.map((m) => row[m] || 0), row.total]);

      const totals = ['', ...selectedMachines.map((m) => rows.reduce((sum, row) => sum + (row[selectedMachines.indexOf(m) + 1] || 0), 0)), rows.reduce((sum, row) => sum + row[row.length - 1], 0)];

      return {
        tableRows: rows,
        tableHeadings: ['Period', ...selectedMachines, 'Total'],
        tableTotals: totals
      };
    }
  }, [tableData, selectedMachines, hasCountryBreakdown]);

  const tooltipStyle = {
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    color: '#374151',
    fontSize: '11px',
    padding: '8px'
  };

  return (
    <div className="pm-grid">
      {/* Filters */}
      <div className="pm-filters-row">
        <div className="pm-filter-group">
          <label>Machines</label>
          <Popover
            active={machinePopoverActive}
            activator={
              <Button onClick={() => setMachinePopoverActive((v) => !v)} disclosure>
                {machineButtonLabel}
              </Button>
            }
            onClose={() => setMachinePopoverActive(false)}
          >
            <div style={{ padding: '8px 12px', maxHeight: '200px', overflow: 'auto' }}>
              {MACHINE_OPTIONS.map((opt) => (
                <div key={opt.value} style={{ padding: '4px 0' }}>
                  <Checkbox label={opt.label} checked={selectedMachines.includes(opt.value)} onChange={() => toggleMachine(opt.value)} />
                </div>
              ))}
            </div>
          </Popover>
        </div>

        <div className="pm-filter-group">
          <label>Years</label>
          <Popover
            active={yearPopoverActive}
            activator={
              <Button onClick={() => setYearPopoverActive((v) => !v)} disclosure>
                {yearButtonLabel}
              </Button>
            }
            onClose={() => setYearPopoverActive(false)}
          >
            <div style={{ padding: '8px 12px', maxHeight: '200px', overflow: 'auto' }}>
              {yearOptionsWithAll.map((opt) => (
                <div key={opt.value} style={{ padding: '4px 0' }}>
                  <Checkbox label={opt.label} checked={selectedYears.includes(opt.value)} onChange={() => toggleYear(opt.value)} />
                </div>
              ))}
            </div>
          </Popover>
        </div>

        <div className="pm-filter-group">
          <label>Countries</label>
          <Popover
            active={countryPopoverActive}
            activator={
              <Button onClick={() => setCountryPopoverActive((v) => !v)} disclosure>
                {countryButtonLabel}
              </Button>
            }
            onClose={() => {
              setCountryPopoverActive(false);
              setCountrySearch('');
            }}
          >
            <div style={{ padding: '8px 12px', width: '220px' }}>
              <TextField
                label=""
                labelHidden
                placeholder="Search countries..."
                value={countrySearch}
                onChange={setCountrySearch}
                autoComplete="off"
                clearButton
                onClearButtonClick={() => setCountrySearch('')}
              />
            </div>
            <Scrollable style={{ padding: '0 12px 8px 12px', maxHeight: '200px' }}>
              {filteredCountryOptions.map((opt) => (
                <div key={opt.value} style={{ padding: '4px 0' }}>
                  <Checkbox label={opt.label} checked={selectedCountries.includes(opt.value)} onChange={() => toggleCountry(opt.value)} />
                </div>
              ))}
              {filteredCountryOptions.length === 0 && <div style={{ padding: '8px 0', color: '#6b7280', fontSize: '12px' }}>No countries found</div>}
            </Scrollable>
          </Popover>
        </div>

        <div className="pm-filter-group">
          <label>Group by</label>
          <Select label="" labelHidden options={PERIOD_OPTIONS} value={groupBy} onChange={setGroupBy} />
        </div>

        <div className="pm-filter-group">
          <label>Data</label>
          <ButtonGroup variant="segmented">
            <Button pressed={dataMode === 'invoices'} onClick={() => setDataMode('invoices')}>
              Invoices
            </Button>
            <Button pressed={dataMode === 'sales'} onClick={() => setDataMode('sales')}>
              Sales
            </Button>
          </ButtonGroup>
        </div>

        <div className="pm-filter-group pm-total-display">
          <label>Total {dataMode === 'invoices' ? 'Invoiced' : 'Sold'}</label>
          <div className="pm-big-number">{grandTotal}</div>
        </div>
      </div>

      {/* Selected filters display */}
      {(selectedYearLabels.length > 0 || selectedCountryNames.length > 0) && (
        <div className="pm-selected-filters">
          {selectedYearLabels.length > 0 && (
            <div className="pm-filter-tags">
              <span className="pm-filter-tags-label">Years:</span>
              {selectedYearLabels.map((year) => (
                <span key={year} className="pm-filter-tag">
                  {year}
                  <button className="pm-filter-tag-remove" onClick={() => toggleYear(year)}>
                    &times;
                  </button>
                </span>
              ))}
            </div>
          )}
          {selectedCountryNames.length > 0 && (
            <div className="pm-filter-tags">
              <span className="pm-filter-tags-label">Countries:</span>
              {selectedCountryNames.map((name) => {
                const countryId = availableCountries.find((c) => c.name === name)?.id;
                return (
                  <span key={name} className="pm-filter-tag">
                    {name}
                    <button className="pm-filter-tag-remove" onClick={() => toggleCountry(String(countryId))}>
                      &times;
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="pm-tab-loading">
          <Spinner size="small" />
        </div>
      ) : (
        <>
          {/* Chart */}
          <div className="pm-card pm-chart-card">
            <h3>{dataMode === 'invoices' ? 'Invoiced' : 'Sold'} Machines by Period</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="period" stroke="#9ca3af" tick={{ fill: '#6b7280', fontSize: 10 }} />
                <YAxis stroke="#9ca3af" tick={{ fill: '#6b7280', fontSize: 10 }} width={30} />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
                    return (
                      <div style={tooltipStyle}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                        {payload.map((entry, idx) => (
                          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                            <span style={{ color: entry.color }}>{entry.name}:</span>
                            <span>{entry.value}</span>
                          </div>
                        ))}
                        <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 4, paddingTop: 4, fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
                          <span>Total:</span>
                          <span>{total}</span>
                        </div>
                      </div>
                    );
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                {selectedMachines.map((machine) => (
                  <Bar key={machine} dataKey={machine} name={machine} stackId="a" fill={MACHINE_COLORS[machine] || '#6b7280'} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Table */}
          <div className="pm-card">
            <h3>
              {dataMode === 'invoices' ? 'Invoiced' : 'Sold'} Machines{hasCountryBreakdown ? ' by Country' : ' by Period'}
            </h3>
            {hasCountryBreakdown ? (
              <div className="pm-pivot-table-wrapper">
                <table className="pm-pivot-table">
                  <thead>
                    <tr>
                      {tableHeadings.map((h, i) => (
                        <th key={i} className={i >= 2 ? 'pm-numeric' : ''}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row, rowIdx) => {
                      const isSubtotal = row[1] === 'TOTAL';
                      return (
                        <tr key={rowIdx} className={isSubtotal ? 'pm-subtotal-row' : ''}>
                          {row.map((cell, cellIdx) => (
                            <td key={cellIdx} className={cellIdx >= 2 ? 'pm-numeric' : ''}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="pm-grand-total-row">
                      {tableTotals.map((cell, i) => (
                        <td key={i} className={i >= 2 ? 'pm-numeric' : ''}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <DataTable columnContentTypes={tableHeadings.map((_, i) => (i === 0 ? 'text' : 'numeric'))} headings={tableHeadings} rows={tableRows} totals={tableTotals} showTotalsInFooter />
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Main Dashboard Component
export default function PerfectMooseDashboard() {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('sales');
  const [error, setError] = useState(null);

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const yearOptions = useMemo(() => generateYearOptions(), []);

  const hasAccess = useMemo(() => {
    return user?.id && ALLOWED_USER_IDS.includes(user.id);
  }, [user]);

  if (!hasAccess) {
    return <Banner tone="critical">Access denied</Banner>;
  }

  if (error) {
    return <Banner tone="critical">{error}</Banner>;
  }

  return (
    <div className="pm-dashboard">
      <div className="pm-header">
        <div className="pm-header-content">
          <div className="pm-logo">
            <h1>Perfect Moose Dashboard</h1>
          </div>
          <div className="pm-header-controls">
            <div className="pm-tabs">
              <button className={`pm-tab ${activeTab === 'sales' ? 'pm-tab-active' : ''}`} onClick={() => setActiveTab('sales')}>
                Sales dashboard
              </button>
              <button className={`pm-tab ${activeTab === 'invoices' ? 'pm-tab-active' : ''}`} onClick={() => setActiveTab('invoices')}>
                Sales per country
              </button>
            </div>
            {activeTab === 'sales' && (
              <div className="pm-year-select">
                <Select label="" labelHidden options={yearOptions} value={selectedYear} onChange={setSelectedYear} />
              </div>
            )}
          </div>
        </div>
      </div>

      {activeTab === 'sales' && <SalesOverviewTab selectedYear={selectedYear} setSelectedYear={setSelectedYear} yearOptions={yearOptions} />}
      {activeTab === 'invoices' && <InvoiceAnalysisTab yearOptions={yearOptions} />}
    </div>
  );
}
